import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    ArrowUpRight,
    FileText,
    Scale,
    Shield,
} from "lucide-react";
import { POLICY_GROUPS, POLICY_CONTENT, COMPANY_ATTRIBUTION } from "../data/policiesContent";

const DEFAULT_POLICY = "privacy";

const POLICY_IDS = new Set(
    POLICY_GROUPS.flatMap((g) => g.policies)
        .filter((p) => !p.link)
        .map((p) => p.id),
);

export default function PoliciesPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const policyFromUrl = searchParams.get("policy");
    const initialPolicy =
        policyFromUrl && POLICY_IDS.has(policyFromUrl)
            ? policyFromUrl
            : DEFAULT_POLICY;
    const [active, setActive] = useState(initialPolicy);

    useEffect(() => {
        if (policyFromUrl && POLICY_IDS.has(policyFromUrl)) {
            setActive(policyFromUrl);
        }
    }, [policyFromUrl]);

    const selectPolicy = (id) => {
        setActive(id);
        setSearchParams({ policy: id }, { replace: true });
    };

    const activePolicy = POLICY_GROUPS.flatMap((g) => g.policies).find(
        (p) => p.id === active,
    );
    const isExternal = Boolean(activePolicy?.link);
    const c = POLICY_CONTENT[active];

    return (
        <div className="pt-28 lg:pt-32" data-testid="policies-page">
            {/* Heading */}
            <section className="relative py-12 lg:py-16">
                <div className="absolute inset-0 grid-bg opacity-[0.15] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                    <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">
                        $ cat legal
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
                        Legal & Policies
                    </h1>
                    <p className="mt-4 text-zinc-400 font-light max-w-2xl">
                        Terms, privacy, security policies, and compliance
                        documentation for apexbrain.in and the ApexBrain
                        platform.
                    </p>
                    <p className="mt-3 text-sm text-zinc-500 font-light max-w-2xl">
                        {COMPANY_ATTRIBUTION}
                    </p>
                </div>
            </section>

            <section className="relative pb-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        {/* Sidebar */}
                        <aside className="lg:col-span-4 xl:col-span-3">
                            <div className="lg:sticky lg:top-28 space-y-8">
                                {POLICY_GROUPS.map((group) => (
                                    <div key={group.id}>
                                        <p className="font-code text-[10px] uppercase tracking-[0.22em] text-zinc-600 font-semibold mb-3 px-1">
                                            {group.label}
                                        </p>
                                        <nav
                                            className="space-y-1"
                                            data-testid={`policies-group-${group.id}`}
                                        >
                                            {group.policies.map((policy) => {
                                                const isActive =
                                                    active === policy.id;
                                                const className = `w-full text-left flex items-center justify-between gap-2 rounded-xl px-3.5 py-2.5 text-sm transition ${
                                                    isActive
                                                        ? "bg-white/[0.08] border border-white/15 text-white font-medium"
                                                        : "text-zinc-400 hover:text-white hover:bg-white/[0.04] border border-transparent"
                                                }`;

                                                if (policy.link) {
                                                    return (
                                                        <Link
                                                            key={policy.id}
                                                            to={policy.link}
                                                            className={className}
                                                            data-testid={`policies-link-${policy.id}`}
                                                        >
                                                            <span className="flex items-center gap-2 min-w-0">
                                                                <ArrowUpRight className="w-3.5 h-3.5 shrink-0 text-electric" />
                                                                <span className="truncate">
                                                                    {policy.label}
                                                                </span>
                                                            </span>
                                                        </Link>
                                                    );
                                                }

                                                return (
                                                    <button
                                                        key={policy.id}
                                                        type="button"
                                                        onClick={() =>
                                                            selectPolicy(
                                                                policy.id,
                                                            )
                                                        }
                                                        className={className}
                                                        data-testid={`policies-tab-${policy.id}`}
                                                    >
                                                        <span className="flex items-center gap-2 min-w-0">
                                                            <FileText className="w-3.5 h-3.5 shrink-0 opacity-60" />
                                                            <span className="truncate">
                                                                {policy.label}
                                                            </span>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </nav>
                                    </div>
                                ))}

                                <div className="rounded-2xl border border-white/[0.08] bg-obsidian-200/60 p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Scale className="w-4 h-4 text-amber-glow" />
                                        <span className="font-display text-sm font-semibold text-white">
                                            Need a signed copy?
                                        </span>
                                    </div>
                                    <p className="text-xs text-zinc-500 leading-relaxed font-light">
                                        DPAs, BAAs, and custom MSAs available
                                        for Business and Enterprise. Email{" "}
                                        <a
                                            href="mailto:legal@apexbrain.in"
                                            className="text-electric hover:underline font-mono"
                                        >
                                            legal@apexbrain.in
                                        </a>
                                        .
                                    </p>
                                </div>
                            </div>
                        </aside>

                        {/* Content */}
                        <div className="lg:col-span-8 xl:col-span-9 min-w-0">
                            {isExternal ? (
                                <div className="rounded-2xl border border-white/[0.08] bg-obsidian-200/50 p-8 sm:p-10 text-center">
                                    <Shield className="w-10 h-10 text-electric mx-auto mb-4" />
                                    <h2 className="font-display text-2xl font-semibold text-white">
                                        {activePolicy?.label}
                                    </h2>
                                    <p className="mt-3 text-sm text-zinc-400 font-light max-w-md mx-auto">
                                        Full security architecture, certifications,
                                        and compliance frameworks live on our
                                        dedicated trust pages.
                                    </p>
                                    <Link
                                        to={activePolicy.link}
                                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep px-5 py-2.5 rounded-full hover:brightness-110 transition"
                                    >
                                        Open {activePolicy.label}
                                        <ArrowUpRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            ) : (
                                c && (
                                    <article data-testid="policies-content">
                                        <div className="mb-8 pb-8 border-b border-white/[0.06]">
                                            <p className="font-code text-[11px] text-zinc-600 mb-3">
                                                {c.updated}
                                            </p>
                                            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight">
                                                {c.title}
                                            </h2>
                                        </div>

                                        <div className="space-y-8">
                                            {c.sections.map((s, i) => (
                                                <div
                                                    key={i}
                                                    className="border-l border-white/[0.08] pl-5"
                                                >
                                                    <h3 className="font-display text-lg font-semibold text-white mb-2">
                                                        {s.h}
                                                    </h3>
                                                    <p className="text-zinc-400 text-sm leading-relaxed font-light">
                                                        {s.p}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-14 p-6 rounded-2xl bg-obsidian-200 border border-white/[0.08]">
                                            <p className="text-sm text-zinc-500 font-light">
                                                {COMPANY_ATTRIBUTION}
                                            </p>
                                        </div>

                                        <div className="mt-6 p-6 rounded-2xl bg-obsidian-200 border border-white/[0.08]">
                                            <h3 className="font-display text-base font-semibold text-white">
                                                Questions about this policy?
                                            </h3>
                                            <p className="text-sm text-zinc-400 mt-1 font-light">
                                                Contact{" "}
                                                <a
                                                    href="mailto:legal@apexbrain.in"
                                                    className="text-electric hover:underline font-mono"
                                                >
                                                    legal@apexbrain.in
                                                </a>{" "}
                                                or{" "}
                                                <a
                                                    href="mailto:privacy@apexbrain.in"
                                                    className="text-electric hover:underline font-mono"
                                                >
                                                    privacy@apexbrain.in
                                                </a>
                                                . We respond within one business
                                                day.
                                            </p>
                                        </div>
                                    </article>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
